import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import PaymentForm from 'features/onboarding/components/PaymentForm';
import UncoveLogin from 'features/onboarding/components/UncoveLogin';
import CircularProgress from '@mui/material/CircularProgress';
import ReportModal from 'shared/modals/ReportModal';
import { Helmet } from 'react-helmet';
import {
  FaChartBar,
  FaCog,
  FaComments,
  FaCreditCard,
  FaEnvelope,
  FaFileInvoice,
  FaFlag,
  FaGift,
  FaPause,
  FaPlay,
  FaPlus,
  FaQuestionCircle,
  FaSearch,
  FaSignOutAlt,
  FaTools,
  FaUsers,
} from 'react-icons/fa';
import ChatPreview from 'features/chat/ChatPreview';
import { decodeJwt, getUserTypeFromJwt, isAdminPayload } from 'utils/jwt';
import { hasSubAccountPermission } from 'utils/permissions';
import ActionMenu from 'shared/ui/ActionMenu';
import Button from 'shared/ui/Button';
import Modal from 'shared/ui/Modal';
import PageShell from 'shared/ui/PageShell';
import StatusPill from 'shared/ui/StatusPill';
import { MODELS_API_BASE_URL, STATS_API_BASE_URL } from 'config/apiUrls';
import {
  formatMessagesRemaining,
  getMinLevenshteinDistance,
  normalizeIdValue,
  normalizeSearchValue
} from './profileUtils';

import './Profile.css';

const getOfferTitle = (offers, offerKeyname) =>
  offers.find((offer) => offer.keyname === offerKeyname)?.title || offerKeyname || 'Offre';

/* Queue du border beam : halos successifs de plus en plus faibles,
   décalés dans le temps le long du contour (le plus en retard ferme la traînée). */
const BEAM_TRAIL = [
  { size: 120, alpha: 0.4, delay: '-0.54s' },
  { size: 140, alpha: 0.3, delay: '-0.46s' },
  { size: 152, alpha: 0.24, delay: '-0.37s' },
  { size: 164, alpha: 0.18, delay: '-0.27s' },
  { size: 176, alpha: 0.12, delay: '-0.16s' },
  { size: 188, alpha: 0.07, delay: '0s' },
];


const MAX_VISIBLE_SUB_ACCOUNTS = 2;

const formatAgencyCurrency = (amount) => new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR'
}).format(Number(amount) || 0);

const formatAgencyNumber = (value) => new Intl.NumberFormat('fr-FR').format(Number(value) || 0);

const getIdVariants = (value) => {
  if (value === null || value === undefined) return [];
  const id = String(value);
  if (!id) return [];
  const suffix = id.split('/').pop();
  return Array.from(new Set([id, suffix, suffix ? `users/${suffix}` : null].filter(Boolean)));
};

const getParisDateInputValue = (date = new Date()) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Paris',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter.formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    if (year && month && day) {
      return `${year}-${month}-${day}`;
    }
  } catch (error) {
    // Keep the dashboard usable if the runtime lacks timezone formatting.
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Profile = (props) => {
  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [newBotMode, setNewBotMode] = useState(false);
  const [uncoveName, setUncoveName] = useState("");
  const [uncoveToken, setUncoveToken] = useState("");
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showUncoveLogin, setShowUncoveLogin] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showGlobalChat, setShowGlobalChat] = useState(false);
  const [error, setError] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [offerSelected, setOfferSelected] = useState(-1);
  const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(false);
  const [isBillingPortalLoading, setIsBillingPortalLoading] = useState(false);
  const [billingPortalError, setBillingPortalError] = useState('');
  const [subscriptionSearch, setSubscriptionSearch] = useState('');
  const [isSubscriptionSearchOpen, setIsSubscriptionSearchOpen] = useState(false);
  const [expandedSubAccountIds, setExpandedSubAccountIds] = useState({});
  const [agentStartIssue, setAgentStartIssue] = useState(null);
  const [agencyStats, setAgencyStats] = useState({ todaySales: 0, todaySalesCount: 0, topChatters: [] });
  const [isAgencyStatsLoading, setIsAgencyStatsLoading] = useState(false);
  const [agencyStatsError, setAgencyStatsError] = useState('');
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const location = useLocation();
  const currentJwtPayload = useMemo(() => decodeJwt(Cookies.get('user_token')), []);
  const editSubscriptionId = useMemo(
    () => new URLSearchParams(location.search || '').get('editSubscription'),
    [location.search]
  );

  const getCurrentUserId = () => {
    if (user?.id || user?._id || user?.userId) {
      return user.id || user._id || user.userId;
    }

    return currentJwtPayload?.id || currentJwtPayload?.userId || currentJwtPayload?.sub || currentJwtPayload?._id || null;
  };

  const currentUserId = normalizeIdValue(getCurrentUserId());
  const subscriptionList = useMemo(
    () => (Array.isArray(subscriptions) ? subscriptions : []),
    [subscriptions]
  );
  const hasSubscriptionUnpaid = Boolean(currentUserId) && subscriptionList.some(
    (subscription) =>
      subscription.has_subscription_unpaid === true &&
      normalizeIdValue(subscription.userId) === currentUserId
  );

  const summupRef = useRef(null);
  const subscriptionSearchRef = useRef(null);
  const handledEditSubscriptionRef = useRef(null);

  const getCurrentUserType = () => {
    const jwt = Cookies.get('user_token');
    if (!jwt) return null;
    return getUserTypeFromJwt(jwt);
  };

  const currentUserType = getCurrentUserType();
  const isJwtAdmin = isAdminPayload(currentJwtPayload);
  const isAgencyUser = currentUserType === 'agence';
  const canOpenDevTools = isJwtAdmin || isAdminPayload(user);
  const showAgencyModelFeatures =
    !currentUserType || currentUserType === 'agence' || currentUserType === 'model';
  const isChatterUser = currentUserType === 'chatter';
  const shouldShowSubscriptionSearch = subscriptionList.length > 10;
  const agencyModelIds = useMemo(() => {
    const ids = subscriptionList.map((subscription) => subscription?.creator?.id).filter(Boolean);
    return Array.from(new Set(ids));
  }, [subscriptionList]);
  const agencySummaryCounts = useMemo(() => {
    const chatterCount = subscriptionList.reduce((count, subscription) => (
      count + (subscription?.sub_accounts ? Object.values(subscription.sub_accounts).length : 0)
    ), 0);
    const unpaidSubscriptionCount = subscriptionList.filter(
      (subscription) =>
        subscription?.has_subscription_unpaid === true ||
        subscription?.has_commission_unpaid === true
    ).length;

    return {
      modelCount: agencyModelIds.length,
      chatterCount,
      unpaidCount: unpaidSubscriptionCount + unpaidInvoices.length,
    };
  }, [agencyModelIds.length, subscriptionList, unpaidInvoices.length]);
  const hasAgencyChatters = agencySummaryCounts.chatterCount > 0;
  const agencyChatterMailById = useMemo(() => {
    const map = {};
    subscriptionList.forEach((subscription) => {
      if (!subscription?.sub_accounts) return;
      Object.entries(subscription.sub_accounts).forEach(([subAccountId, subAccount]) => {
        const mail = subAccount?.mail;
        if (!mail) return;
        [subAccountId, subAccount?.id].forEach((id) => {
          getIdVariants(id).forEach((variant) => {
            map[variant] = mail;
          });
        });
      });
    });
    return map;
  }, [subscriptionList]);
  const getAgencyChatterLabel = (senderName) => {
    const fallback = String(senderName || 'Inconnu').split('/').pop();
    return agencyChatterMailById[senderName] || agencyChatterMailById[fallback] || agencyChatterMailById[`users/${fallback}`] || fallback;
  };
  const canAccessGlobalStats = useMemo(
    () => subscriptionList.some(
      (subscription) =>
        subscription?.is_sub_account !== true ||
        hasSubAccountPermission(subscription?.sub_account_permissions, 'can_view_stats', isJwtAdmin)
    ),
    [isJwtAdmin, subscriptionList]
  );
  const chatEnabledSubscriptions = useMemo(
    () => subscriptionList.filter(subscription =>
      subscription?.is_sub_account === true &&
      hasSubAccountPermission(subscription?.sub_account_permissions, 'can_chat', isJwtAdmin) &&
      subscription?.has_subscription_unpaid !== true &&
      subscription?.has_commission_unpaid !== true
    ),
    [isJwtAdmin, subscriptionList]
  );
  const canOpenGlobalChat = isChatterUser && chatEnabledSubscriptions.length > 0;
  const isGlobalChatSplitActive = showGlobalChat && canOpenGlobalChat;
  useEffect(() => {
    const className = 'global-chat-split-active';
    if (typeof document === 'undefined') return undefined;

    document.body.classList.toggle(className, isGlobalChatSplitActive);

    return () => {
      document.body.classList.remove(className);
    };
  }, [isGlobalChatSplitActive]);

  useEffect(() => {
    if (isSubscriptionSearchOpen) {
      subscriptionSearchRef.current?.focus();
    }
  }, [isSubscriptionSearchOpen]);

  useEffect(() => {
    if (!isAgencyUser) {
      setAgencyStats({ todaySales: 0, todaySalesCount: 0, topChatters: [] });
      setAgencyStatsError('');
      setIsAgencyStatsLoading(false);
      return undefined;
    }

    if (!agencyModelIds.length || !currentUserId) {
      setAgencyStats({ todaySales: 0, todaySalesCount: 0, topChatters: [] });
      setAgencyStatsError('');
      setIsAgencyStatsLoading(false);
      return undefined;
    }

    let isCurrent = true;

    const fetchAgencyStats = async () => {
      setIsAgencyStatsLoading(true);
      setAgencyStatsError('');
      const jwt = Cookies.get('user_token');
      const today = getParisDateInputValue();
      const payload = {
        model_ids: agencyModelIds,
        start_date: today,
        end_date: today,
      };
      const config = {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
          'User-Id': currentUserId,
        }
      };

      const requests = [
        axios.post(STATS_API_BASE_URL, {
          ...payload,
          sender: 'all',
        }, config)
      ];
      if (hasAgencyChatters) {
        requests.push(
          axios.post(`${STATS_API_BASE_URL}/top_chatters`, {
            ...payload,
            limit: 5,
          }, config)
        );
      }

      const [salesResult, topChattersResult] = await Promise.allSettled(requests);

      if (!isCurrent) return;

      const nextStats = {
        todaySales: 0,
        todaySalesCount: 0,
        topChatters: [],
      };
      let hasStatsError = false;

      if (salesResult.status === 'fulfilled' && salesResult.value?.data?.status === 'success') {
        nextStats.todaySales = salesResult.value.data.data?.total_sales || 0;
        nextStats.todaySalesCount = salesResult.value.data.data?.total_media_bought || 0;
      } else {
        hasStatsError = true;
      }

      if (hasAgencyChatters && topChattersResult?.status === 'fulfilled' && topChattersResult.value?.data?.status === 'success') {
        nextStats.topChatters = Array.isArray(topChattersResult.value.data.data)
          ? topChattersResult.value.data.data
          : [];
      } else if (hasAgencyChatters) {
        hasStatsError = true;
      }

      setAgencyStats(nextStats);
      setAgencyStatsError(hasStatsError ? 'Stats agence indisponibles pour le moment.' : '');
      setIsAgencyStatsLoading(false);
    };

    fetchAgencyStats().catch((error) => {
      if (!isCurrent) return;
      console.error('Erreur lors du chargement du résumé agence:', error);
      setAgencyStats({ todaySales: 0, todaySalesCount: 0, topChatters: [] });
      setAgencyStatsError('Stats agence indisponibles pour le moment.');
      setIsAgencyStatsLoading(false);
    });

    return () => {
      isCurrent = false;
    };
  }, [agencyModelIds, currentUserId, hasAgencyChatters, isAgencyUser]);

  const filteredSubscriptions = useMemo(() => {
    if (!shouldShowSubscriptionSearch) return subscriptionList;
    const query = normalizeSearchValue(subscriptionSearch);
    if (!query) return subscriptionList;
    const maxDistance = Math.max(1, Math.ceil(query.length * 0.4));

    const scored = subscriptionList.map((subscription) => {
      const searchText = [
        subscription?.creator?.name,
        subscription?.creator?.id
      ].filter(Boolean).join(' ');
      const distance = getMinLevenshteinDistance(searchText, query);
      return { subscription, distance };
    }).filter(item => item.distance <= maxDistance);

    scored.sort((a, b) => a.distance - b.distance);
    return scored.map(item => item.subscription);
  }, [subscriptionList, subscriptionSearch, shouldShowSubscriptionSearch]);

  useEffect(() => {
    fetchCurrentUser();
    fetchSubscriptionData();
  }, []);

  useEffect(() => {
    if (
      !editSubscriptionId ||
      subscriptionList.length === 0 ||
      handledEditSubscriptionRef.current === editSubscriptionId
    ) {
      return;
    }

    const targetSubscription = subscriptionList.find(
      (subscription) => normalizeIdValue(subscription?._id) === normalizeIdValue(editSubscriptionId)
    );

    if (!targetSubscription) return;

    handledEditSubscriptionRef.current = editSubscriptionId;
    if (targetSubscription.is_sub_account === true) return;

    updatePlan(targetSubscription);
    navigate('/', { replace: true });
  }, [editSubscriptionId, subscriptionList, navigate]);

  useEffect(() => {
    if (
      offerSelected > -1 &&
      summupRef.current
    ) {
      summupRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [offerSelected, props.offers]);

  const fetchCurrentUser = async () => {
    const jwt = Cookies.get('user_token');

    const config = {
      headers: {
        Authorization: `Bearer ${jwt}`
      }
    };

    try {
        const response = await axios.get('https://unbot.io/api/v1.0/auth/self', config);
        if (response?.data?.mail) {
          setUser(response.data);
        }
    } catch (error) {
        console.error("Error fetching user data:", error);
    }    
  }

  const fetchSubscriptionData = async () => {
    setIsSubscriptionsLoading(true);
    const jwt = Cookies.get('user_token');

    const config = {
      headers: {
        Authorization: `Bearer ${jwt}`
      }
    };

    try {
        const response = await axios.get(MODELS_API_BASE_URL, config);
        const nextSubscriptions = Array.isArray(response.data) ? response.data : [];
        setSubscriptions(nextSubscriptions);

        if (nextSubscriptions.length > 0) {//&& nextSubscriptions.some(sub => sub.has_commission_unpaid)
          fetchUnpaidInvoices(jwt);
        }
    } catch (error) {
        console.error("Error fetching subscription data:", error);
    } finally {
      setIsSubscriptionsLoading(false);
    }
  };

  const fetchUnpaidInvoices = async (jwt) => {
    try {
      const config = {
        headers: {
          'Authorization': `Bearer ${jwt}`
        }
      };
      const response = await axios.get('https://unbot.io/api/v1.0/invoice/unpaid', config);
      if (response.data && Array.isArray(response.data.invoices)) {
        setUnpaidInvoices(response.data.invoices);
      }
    } catch (error) {
      console.error("Error fetching unpaid invoices:", error);
    }
  };

  const openBillingPortal = async () => {
    setBillingPortalError('');
    setIsBillingPortalLoading(true);
    const jwt = Cookies.get('user_token');

    const config = {
      headers: {
        Authorization: `Bearer ${jwt}`
      }
    };

    try {
      const response = await axios.post('https://unbot.io/api/v1.0/subscription/billing-portal', {}, config);
      const portalUrl = response?.data?.url || response?.data?.billingPortalUrl || response?.data?.link;
      if (portalUrl) {
        // redirect to the portal url
        window.location.href = portalUrl;
      } else {
        setBillingPortalError("Impossible d'ouvrir le portail de paiement.");
      }
    } catch (error) {
      console.error('Error opening billing portal', error);
      setBillingPortalError("Impossible d'ouvrir le portail de paiement pour le moment.");
    } finally {
      setIsBillingPortalLoading(false);
    }
  };


  const handleUncoveLoginSuccess = (username, token) => {
    setUncoveName(username);
    setUncoveToken(token);
    setShowUncoveLogin(false);
    setShowPaymentForm(true);
  }

  const addBot = async (cardholderName, address, vatNumber, paymentMethodId) => {
    const jwt = Cookies.get('user_token');

    const config = {
      headers: {
        Authorization: `Bearer ${jwt}`
      }
    };

    try {
      let offerKeyname = props.offers[offerSelected].keyname;
      let params = {
        userName: uncoveName,
        userToken: uncoveToken,
        offerKeyname
      }

      if (offerKeyname === "free") {
        activeSubscription(params, config);
        return;
      }

      let paymentData = {
        userName: uncoveName,
        cardholderName,
        address,
        vatNumber,
        paymentMethodId,
        offerKeyname: props.offers[offerSelected].keyname
      }

      let response = await axios.post('https://unbot.io/api/v1.0/subscription/pay',
        paymentData,
        config
      );

      let payResult = response.data;

      params.paymentMethodId = paymentMethodId;

      // No 3D secure needed
      if (payResult.status === "success") {
        activeSubscription(params, config);
      }

      // 3D secure needed
      if (payResult.status === "requiresConfirmation") {
        let stripeResult;
        if (payResult.type === "setup") {
          // Setup 3D secure for trial period
          stripeResult = await stripe.confirmCardSetup(payResult.clientSecret, {
            payment_method: {
              card: elements.getElement(CardElement),
            },
          });

          params.paymentMethodId = stripeResult.setupIntent.payment_method;
        } else if(payResult.type === "payment") {
          // Setup 3D secure for direct card paiement
          stripeResult = await stripe.confirmCardPayment(payResult.clientSecret, {
            payment_method: {
              card: elements.getElement(CardElement),
            },
          });

          params.stripeSubscriptionId = payResult.stripeSubscriptionId;
          if (stripeResult.paymentIntent) {
            params.paymentMethodId = stripeResult.paymentIntent.payment_method;
          }
        } else {
          setError('Le paiement a renvoyé une réponse inattendue. Merci de réessayer.');
          setIsLoading(false);
          return;
        }

        if (stripeResult?.error?.message) {
          setError(stripeResult.error.message);
          setIsLoading(false);
        } else {
          activeSubscription(params, config);
        }
      }

      if (payResult.error) {
        setIsLoading(false);
        setError(payResult.error.message ?? payResult.error.raw.message);
      }
    } catch (error) {
      setIsLoading(false);
      setError('Une erreur est survenue pendant le paiement. Merci de réessayer.');
    }
  }

  const activeSubscription = async (params, config) => {
    let response = await axios.post('https://unbot.io/api/v1.0/subscription',
      params, config
    );

    if (response?.data?.error?.message) {
      setError(response?.data?.error?.message);
      return;
    }

    // if (!user.trialPeriodUsed) {
    //   user.trialPeriodUsed = true;
    //   setUser(user);
    // }

    setIsLoading(false);
    fetchSubscriptionData();
    setUncoveName("");
    setUncoveToken("");
    setNewBotMode(false);
    setShowPaymentForm(false);
    setError("");
  }

  function updatePlan(subscription) {
    setNewBotMode(true);
    setUncoveName(subscription.creator.name);
    setUncoveToken(subscription.token);
    setShowPaymentForm(true);
    setCurrentSubscription(subscription)
    // setCurrentPlanIndex(
    //   props.offers[props.offers.map(e => e.keyname).indexOf(subscription.offerKeyname)].index
    // );
  }

  const handleAgentStatusToggle = async (subscription) => {
    setIsLoading(true);
    setAgentStartIssue(null);
    const jwt = Cookies.get('user_token');
    const config = {
      headers: { Authorization: `Bearer ${jwt}` }
    };

    try {
      const response = await axios.patch(`${MODELS_API_BASE_URL}/${encodeURIComponent(subscription._id)}`,
        { monitor_status: !subscription.monitor_status },
        config
      );
      const error = response?.data?.error;
      if (error?.key === 'invalidMediaConfiguration' || error?.key === 'mediaConfigurationCheckFailed') {
        setAgentStartIssue({
          subscriptionId: subscription._id,
          message: error.message,
        });
        return;
      }
      if (error?.message) {
        setError(error.message);
        return;
      }
      fetchSubscriptionData();
    } catch (error) {
      console.error('Erreur lors du changement de statut de l\'agent :', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddModelClick = () => {
    setNewBotMode(true);
    setShowUncoveLogin(true);
  };

  const toggleSubAccounts = (subscriptionId) => {
    setExpandedSubAccountIds((current) => ({
      ...current,
      [subscriptionId]: !current[subscriptionId],
    }));
  };

  const accountActions = [
    !isChatterUser && subscriptionList.length > 0 && {
      label: 'Factures',
      icon: <FaFileInvoice />,
      onClick: () => navigate('/invoices'),
    },
    !isChatterUser && subscriptionList.length > 0 && {
      label: isBillingPortalLoading ? 'Ouverture…' : 'Paiement',
      icon: <FaCreditCard />,
      disabled: isBillingPortalLoading,
      onClick: openBillingPortal,
    },
    canAccessGlobalStats && {
      label: 'Stats globales',
      icon: <FaChartBar />,
      onClick: () => navigate('/stats'),
    },
    {
      label: 'Paramètres',
      icon: <FaCog />,
      onClick: () => navigate('/settings'),
    },
    showAgencyModelFeatures && {
      label: 'Parrainage',
      icon: <FaGift />,
      onClick: () => navigate('/sponsor'),
    },
    !isChatterUser && {
      label: 'Aide',
      icon: <FaQuestionCircle />,
      onClick: () => navigate('/helpdesk'),
    },
    canOpenDevTools && {
      label: 'Dev tools',
      icon: <FaTools />,
      onClick: () => navigate('/dev-tools'),
    },
    {
      label: 'Signaler un problème',
      icon: <FaFlag />,
      onClick: () => setShowReportModal(true),
    },
    {
      label: 'Déconnexion',
      icon: <FaSignOutAlt />,
      tone: 'danger',
      onClick: props.onLogoutButtonClicked,
    },
  ];

  return (
    <div className="profile-container">
      <Helmet>
        <title>Profil Unbot</title>
        <meta name="description" content="Accédez à votre profil Unbot" />
        <meta name="keywords" content="profil Unbot, abonnements IA, configuration agent Uncove" />
      </Helmet>
      {
        isLoading &&
        <div className="loading-container">
          <div className="loading-box">
            <CircularProgress color="primary" />
          </div>
        </div>
      }

      {
        showGlobalChat && canOpenGlobalChat ? (
          <ChatPreview
            onBack={() => setShowGlobalChat(false)}
            subscriptions={chatEnabledSubscriptions}
            isGlobalChat
            splitView
            title="Chat général"
          />
        ) :
        (
          <>
            {
              !newBotMode && <>
                <PageShell
                  title="Dashboard"
                  actions={(
                    <div className="profile-header-actions">
                      <ActionMenu label="Actions du compte" items={accountActions} />
                    </div>
                  )}
                >
                  {canOpenGlobalChat && (
                    <div className="global-chat-entry">
                      <button
                        type="button"
                        className="global-chat-button"
                        onClick={() => setShowGlobalChat(true)}
                      >
                        <span className="global-chat-icon" aria-hidden="true">
                          <FaComments />
                        </span>
                        <span className="global-chat-button-copy">
                          <span className="global-chat-button-main">Chat général</span>
                          <span className="global-chat-button-count">
                            {chatEnabledSubscriptions.length} modèle
                            {chatEnabledSubscriptions.length > 1 ? 's' : ''}
                          </span>
                        </span>
                        <span className="global-chat-button-action">Ouvrir</span>
                      </button>
                    </div>
                  )}
                </PageShell>

                <section className="subscription-dashboard-section">
                  {isAgencyUser && (
                    <div className="agency-summary-panel" aria-label="Résumé agence">
                      <div className="agency-summary-header">
                        <div>
                          <span className="agency-summary-eyebrow">Agence</span>
                          <h2>Résumé agence</h2>
                        </div>
                        {isAgencyStatsLoading && (
                          <span className="agency-summary-loading">Chargement</span>
                        )}
                      </div>
                      <div className="agency-summary-grid">
                        <div className="agency-summary-card agency-summary-card-primary">
                          <span className="agency-summary-label">Ventes aujourd'hui</span>
                          <strong>{formatAgencyCurrency(agencyStats.todaySales)}</strong>
                        </div>
                        <div className="agency-summary-card">
                          <span className="agency-summary-label">Modèles</span>
                          <strong>{formatAgencyNumber(agencySummaryCounts.modelCount)}</strong>
                        </div>
                        <div className="agency-summary-card">
                          <span className="agency-summary-label">Nombre de ventes</span>
                          <strong>{formatAgencyNumber(agencyStats.todaySalesCount)}</strong>
                        </div>
                        <div className="agency-summary-card">
                          <span className="agency-summary-label">Impayés</span>
                          <strong>{formatAgencyNumber(agencySummaryCounts.unpaidCount)}</strong>
                        </div>
                      </div>
                      {hasAgencyChatters && agencyStats.topChatters.length > 0 && (
                        <div className="agency-top-chatters">
                          <div className="agency-top-chatters-title">Top chatters</div>
                          <ol>
                            {agencyStats.topChatters.map((chatter) => (
                              <li key={chatter.sender_name}>
                                <span>{getAgencyChatterLabel(chatter.sender_name)}</span>
                                <strong>{formatAgencyCurrency(chatter.total_sales)}</strong>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                      {agencyStatsError && (
                        <div className="agency-summary-error">{agencyStatsError}</div>
                      )}
                    </div>
                  )}

                  {subscriptionList.length > 0 && (
                    <div className="dashboard-section-heading">
                      <div>
                        <div className="subscription-heading-row">
                          <h2>Mes comptes</h2>
                          {showAgencyModelFeatures && (
                            <Button
                              className="dashboard-add-model-button"
                              variant="primary"
                              size="small"
                              icon={<FaPlus />}
                              onClick={handleAddModelClick}
                            >
                              Ajouter un modèle
                            </Button>
                          )}
                          {shouldShowSubscriptionSearch && (
                            <div className={`subscription-search-shell ${isSubscriptionSearchOpen ? 'is-open' : ''}`}>
                              <div className="subscription-search-control">
                                <button
                                  type="button"
                                  className="subscription-search-icon"
                                  aria-label={isSubscriptionSearchOpen ? 'Fermer la recherche' : 'Ouvrir la recherche'}
                                  aria-expanded={isSubscriptionSearchOpen}
                                  onClick={() => {
                                    setIsSubscriptionSearchOpen((current) => {
                                      if (current) {
                                        setSubscriptionSearch('');
                                      }
                                      return !current;
                                    });
                                  }}
                                >
                                  <FaSearch aria-hidden="true" />
                                </button>
                                <input
                                  ref={subscriptionSearchRef}
                                  type="text"
                                  value={subscriptionSearch}
                                  onChange={(e) => setSubscriptionSearch(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      setSubscriptionSearch('');
                                      setIsSubscriptionSearchOpen(false);
                                    }
                                  }}
                                  placeholder="Rechercher"
                                  aria-label="Rechercher un modèle"
                                />
                                {subscriptionSearch && (
                                  <button
                                    type="button"
                                    className="subscription-search-clear"
                                    onClick={() => setSubscriptionSearch('')}
                                    aria-label="Effacer la recherche"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {shouldShowSubscriptionSearch && subscriptionSearch.trim().length > 0 && filteredSubscriptions.length === 0 && (
                    <div className="subscription-search-empty">
                      Aucun modèle trouvé pour "{subscriptionSearch}".
                    </div>
                  )}

                {isSubscriptionsLoading ? (
                  <div className="dashboard-loading">
                    <CircularProgress color="primary" />
                  </div>
                ) : (
                  <>
                    {hasSubscriptionUnpaid && (
                      <div className="unpaid-invoice-banner">
                        <div className="unpaid-invoice-content">
                          <h4>Paiement d'abonnement en retard</h4>
                          <p>
                            Un ou plusieurs abonnements ont un paiement en retard. Merci de régulariser pour réactiver l'agent.
                          </p>
                        </div>
                        <div className="unpaid-invoice-actions">
                          <button
                            onClick={openBillingPortal}
                            className="new-bot-button"
                            disabled={isBillingPortalLoading}
                          >
                            {isBillingPortalLoading ? 'Ouverture du portail Stripe…' : 'Payer sur Stripe'}
                          </button>
                        </div>
                      </div>
                    )}

                    {unpaidInvoices.length > 0 && (
                      <div className="unpaid-invoice-banner">
                        <div className="unpaid-invoice-content">
                          <h4>Facture impayée</h4>
                          <p>
                            Vous avez une ou plusieurs factures en attente de paiement. Veuillez les régler pour continuer à utiliser nos services sans interruption.
                          </p>
                          <small>
                            Après paiement, le statut peut mettre jusqu'à 1 heure à se mettre à jour. Si le problème persiste, veuillez contacter le <Link to="/helpdesk">service client</Link>.
                          </small>
                        </div>
                        <div className="unpaid-invoice-actions">
                          {unpaidInvoices[0].payment_link && (
                            <a href={unpaidInvoices[0].payment_link} target="_blank" rel="noopener noreferrer" className="new-bot-button">
                              Régler
                            </a>
                          )}
                          <button onClick={() => navigate('/invoices')} className="button-outline">
                            Voir les factures
                          </button>
                        </div>
                      </div>
                    )}

                    {
                      subscriptionList.length === 0 && (
                        showAgencyModelFeatures ? (
                          <div className="dashboard-empty-state">
                            <div className="empty-onboarding-main">
                              <span className="empty-onboarding-eyebrow">Premier compte</span>
                              <h3>Lancez votre premier compte Unbot</h3>
                              <p>
                                Connectez un compte Uncove, puis choisissez votre fonctionnement : IA, chatters, ou les deux selon votre organisation.
                              </p>
                              <Button
                                className="empty-onboarding-cta"
                                icon={<FaPlus />}
                                onClick={handleAddModelClick}
                              >
                                Ajouter un compte
                              </Button>
                            </div>
                            <div className="empty-onboarding-steps" aria-label="Étapes de démarrage">
                              <div className="empty-onboarding-step">
                                <span className="empty-step-icon" aria-hidden="true"><FaPlus /></span>
                                <div className="empty-onboarding-step-copy">
                                  <strong>Connecter</strong>
                                  <span>Reliez le compte Uncove à gérer dans le dashboard.</span>
                                </div>
                              </div>
                              <div className="empty-onboarding-step">
                                <span className="empty-step-icon" aria-hidden="true"><FaUsers /></span>
                                <div className="empty-onboarding-step-copy">
                                  <strong>Organiser</strong>
                                  <span>Activez l’IA, affectez des chatters, ou combinez les deux.</span>
                                </div>
                              </div>
                              <div className="empty-onboarding-step">
                                <span className="empty-step-icon" aria-hidden="true"><FaComments /></span>
                                <div className="empty-onboarding-step-copy">
                                  <strong>Piloter</strong>
                                  <span>Suivez les messages, les ventes et l’activité du compte.</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          isChatterUser && (
                            <div className="dashboard-empty-state">
                              <div className="empty-onboarding-main">
                                <span className="empty-onboarding-eyebrow">Mode chatter</span>
                                <h3>Aucun modèle pour le moment</h3>
                                <p>
                                  Vous êtes actuellement en mode chatter, destiné à être rattaché au compte d’une agence ou d’un propriétaire de modèle.
                                </p>
                                <p>
                                  Transmettez votre adresse e-mail au propriétaire du modèle ou à l’agence avec qui vous êtes en contact pour qu’il vous ajoute à son compte.
                                </p>
                                <p className="empty-onboarding-email"><strong>{user?.mail || 'Email non disponible'}</strong></p>
                                <p>
                                  Pour ajouter votre propre compte ou changer le mode de votre compte, rendez-vous dans les paramètres.
                                </p>
                                <Button
                                  className="empty-onboarding-settings"
                                  variant="secondary"
                                  size="small"
                                  icon={<FaCog />}
                                  onClick={() => navigate('/settings')}
                                >
                                  Ouvrir les paramètres
                                </Button>
                              </div>
                              <div className="empty-onboarding-steps" aria-label="Étapes pour être ajouté à un modèle">
                                <div className="empty-onboarding-step">
                                  <span className="empty-step-icon" aria-hidden="true"><FaEnvelope /></span>
                                  <div className="empty-onboarding-step-copy">
                                    <strong>Envoyer votre email</strong>
                                    <span>Partagez cette adresse avec l’agence ou le propriétaire du modèle.</span>
                                  </div>
                                </div>
                                <div className="empty-onboarding-step">
                                  <span className="empty-step-icon" aria-hidden="true"><FaUsers /></span>
                                  <div className="empty-onboarding-step-copy">
                                    <strong>Attendre l’ajout</strong>
                                    <span>Il vous suffit d’attendre.</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        )
                      )
                    }
                    {filteredSubscriptions.length > 0 && (
                      <div className="subscription-card-grid">
                        {filteredSubscriptions.map(subscription => {
                        const isCommissionUnpaid = subscription.has_commission_unpaid === true;
                        const isSubscriptionUnpaid = subscription.has_subscription_unpaid === true;
                        const isUnpaid = isCommissionUnpaid || isSubscriptionUnpaid;

                        const isSubAccount = subscription.is_sub_account === true;
                        const permissions = subscription.sub_account_permissions;
                        const canEditAI = !isSubAccount || (isSubAccount && hasSubAccountPermission(permissions, 'can_edit_ai', isJwtAdmin));
                        const canOpenSubscriptionChat = isSubAccount && hasSubAccountPermission(permissions, 'can_chat', isJwtAdmin);
                        const canOpenSubscriptionDetails = canEditAI || (
                          isSubAccount && (
                            hasSubAccountPermission(permissions, 'can_view_stats', isJwtAdmin) ||
                            hasSubAccountPermission(permissions, 'can_use_mass_dm', isJwtAdmin) ||
                            hasSubAccountPermission(permissions, 'can_manage_sub_accounts', isJwtAdmin)
                          )
                        );
                        const hasSubscriptionCardActions = canOpenSubscriptionDetails || canOpenSubscriptionChat;
                        const hasDetailsWithoutAgentControls = canOpenSubscriptionDetails && !canEditAI;
                        const hasChatOnlyAction = canOpenSubscriptionChat && !canOpenSubscriptionDetails;
                        const creatorName = subscription?.creator?.name ?? "";
                        const offerTitle = getOfferTitle(props.offers, subscription.offerKeyname);
                        const subAccounts = subscription.sub_accounts ? Object.values(subscription.sub_accounts) : [];
                        const isSubAccountsExpanded = Boolean(expandedSubAccountIds[subscription._id]);
                        const visibleSubAccounts = isSubAccountsExpanded ? subAccounts : subAccounts.slice(0, MAX_VISIBLE_SUB_ACCOUNTS);

                        const isAgentActive = subscription.monitor_status === true && !isUnpaid;
                        const showChangeSubscriptionAction = isUnpaid && !isSubAccount;
                        const actionContainerClassName = [
                          'subscription-buttons-container',
                          canOpenSubscriptionChat && !hasDetailsWithoutAgentControls && 'subscription-buttons-container-with-chat',
                          hasDetailsWithoutAgentControls && 'subscription-buttons-container-no-agent',
                          hasDetailsWithoutAgentControls && canOpenSubscriptionChat && 'subscription-buttons-container-no-agent-with-chat',
                          hasChatOnlyAction && 'subscription-buttons-container-chat-only',
                          showChangeSubscriptionAction && 'subscription-buttons-container-change-plan',
                          'subscription-buttons-container-without-menu',
                        ].filter(Boolean).join(' ');

                        return <article
                          className={`subscription${isAgentActive ? " subscription-active" : ""}${isUnpaid ? " subscription-disabled" : ""}`}
                          key={subscription._id}
                          title={isUnpaid ? "Cet abonnement est suspendu en raison d'un impayé." : ""}
                        >
                          {isAgentActive && (
                            <div className="subscription-beam" aria-hidden="true">
                              {BEAM_TRAIL.map((segment, index) => (
                                <span
                                  key={index}
                                  className="subscription-beam-tail"
                                  style={{
                                    '--beam-size': `${segment.size}px`,
                                    '--beam-alpha': segment.alpha,
                                    '--beam-delay': segment.delay,
                                  }}
                                />
                              ))}
                              <span className="subscription-beam-head" />
                            </div>
                          )}
                          <div className="subscription-info">
                            <div className="subscription-copy">
                              <div className="subscription-title-row">
                                <span className="subscription-name">{creatorName}</span>
                                <span className="subscription-plan-inline">{offerTitle}</span>
                                {isUnpaid && <StatusPill tone="danger">Suspendu</StatusPill>}
                                {isSubAccount && <StatusPill tone="accent">Chatter</StatusPill>}
                              </div>
                              <div className="subscription-mini-meta" aria-label="Informations rapides de l'abonnement">
                                <span>
                                  <FaEnvelope aria-hidden="true" />
                                  Crédits messages
                                  <strong>{formatMessagesRemaining(subscription.messagesRemaining)}</strong>
                                </span>
                                {!isSubAccount && subAccounts.length > 0 && (
                                  <span>
                                    <FaUsers aria-hidden="true" />
                                    Chatters
                                    <strong>{subAccounts.length}</strong>
                                  </span>
                                )}
                              </div>
                              {subAccounts.length > 0 && (
                                <div className="subscription-subaccounts">
                                  {visibleSubAccounts.map((sa, index) => (
                                    <span key={sa.mail || index} title={sa.mail || 'N/A'}>
                                      {sa.mail || 'N/A'}
                                    </span>
                                  ))}
                                  {subAccounts.length > MAX_VISIBLE_SUB_ACCOUNTS && (
                                    <button
                                      type="button"
                                      className="subscription-subaccounts-more"
                                      aria-expanded={isSubAccountsExpanded}
                                      onClick={() => toggleSubAccounts(subscription._id)}
                                    >
                                      {isSubAccountsExpanded ? 'Réduire' : `+${subAccounts.length - MAX_VISIBLE_SUB_ACCOUNTS}`}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          {hasSubscriptionCardActions && (
                            <div className={actionContainerClassName}>
                              {canEditAI && (
                                showChangeSubscriptionAction ? (
                                  <button
                                    type="button"
                                    className="subscription-status-action subscription-change-plan-action"
                                    onClick={() => updatePlan(subscription)}
                                  >
                                    <FaCreditCard />
                                    <span>Changer d'abonnement</span>
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className={`subscription-status-action ${subscription.monitor_status ? 'is-active' : ''}`}
                                    onClick={() => handleAgentStatusToggle(subscription)}
                                    disabled={isUnpaid}
                                  >
                                    {subscription.monitor_status ? <FaPause /> : <FaPlay />}
                                    <span>{subscription.monitor_status ? 'Pause' : 'Démarrer'}</span>
                                  </button>
                                )
                              )}
                              {canOpenSubscriptionDetails && (
                                <button
                                  type="button"
                                  className="subscription-primary-action"
                                  onClick={() => navigate(`/subscription/${encodeURIComponent(subscription._id)}`)}
                                  disabled={isUnpaid}
                                >
                                  Configurer
                                </button>
                              )}
                              {canOpenSubscriptionChat && (
                                <button
                                  type="button"
                                  className="subscription-chat-action"
                                  onClick={() => navigate(`/subscription/${encodeURIComponent(subscription._id)}/chat?origin=profile`)}
                                  disabled={isUnpaid}
                                >
                                  <FaComments />
                                  <span>Chat</span>
                                </button>
                              )}
                            </div>
                          )}
                        </article>
                        })}
                      </div>
                    )}
                  </>
                )}
                </section>
                {billingPortalError && (
                  <div className="billing-portal-error">{billingPortalError}</div>
                )}
                <ReportModal
                  isOpen={showReportModal}
                  onClose={() => setShowReportModal(false)}
                  userEmail={user?.mail || ''}
                />
                <Modal
                  isOpen={!!agentStartIssue}
                  onClose={() => setAgentStartIssue(null)}
                  title="Configuration des médias requise"
                  size="small"
                  footer={(
                    <>
                      <Button variant="secondary" onClick={() => setAgentStartIssue(null)}>
                        Fermer
                      </Button>
                      <Button
                        onClick={() => {
                          const target = agentStartIssue?.subscriptionId;
                          setAgentStartIssue(null);
                          if (target) {
                            navigate(`/subscription/${encodeURIComponent(target)}/media`);
                          }
                        }}
                      >
                        Gestion des médias
                      </Button>
                    </>
                  )}
                >
                  <p>
                    {agentStartIssue?.message || "Avant de démarrer l'agent, configurez au moins un média avec une description valide et un prix supérieur à 1 euro."}
                  </p>
                </Modal>
              </>
            }

            {
              newBotMode && showUncoveLogin &&
              <div className="new-bot-container">
                <div className="alert">
                    <b>ÉTAPE 1/2</b> <br/><br/>
                    Vous êtes sur la bonne voie 👍 <br/>
                    Lancez Unbot en 2 clics ✨ <br/>
                    Connectez vous avec vos identifiants Uncove 🔒 <br />
                    Une fois le paiement réglé, votre chatting sera automatisé 🚀 <br />
                    Vous pourrez ensuite configurer votre agent ici même depuis votre profil ⚙️
                </div>

                <UncoveLogin
                  onLoginSuccess={handleUncoveLoginSuccess}
                  onCancel={() => {
                    setNewBotMode(false);
                    setShowUncoveLogin(false);
                  }}
                />
              </div>
            }

            {
              newBotMode && showPaymentForm &&
              <div className="new-bot-container">
                {currentSubscription && (
                  <div className="new-bot-top-actions">
                    <button
                      type="button"
                      className="button-outline"
                      onClick={() => {
                        setError('');
                        setNewBotMode(false);
                        setShowPaymentForm(false);
                        setCurrentSubscription(null);
                        setOfferSelected(-1);
                      }}
                    >
                      ← Retour
                    </button>
                  </div>
                )}
                <div className="alert">
                  <b>ÉTAPE 2/2</b> <br/><br/>
                  {
                    !currentSubscription &&
                    <>
                      Bravo 👏 <br/>
                      Vous êtes sur le point de lancer Unbot sur le compte de <b>{uncoveName}</b><br />
                      Une fois le paiement réglé, votre chatting sera automatisé 💬<br />
                      Vous pourrez ensuite configurer votre agent ici même depuis votre profil ⚙️
                    </>
                  }

                  {
                    currentSubscription &&
                    <>
                      Si vous changez d'abonnement, vos crédits non utilisés seront conservés ✅<br/><br/>
                      En changeant d'abonnement, votre ancien abonnement sera supprimé et une nouvelle échéance mensuelle commencera 🔄<br/><br/>
                      Vous pouvez reprendre votre abonnement actuel si vous souhaitez simplement obtenir plus de crédits ➕
                    </>
                  }

                </div>

                <p className="new-bot-title">Offres</p>

                <section className="offer-row">
                  {
                    props.offers
                      .filter(e => e.keyname !== "free" || !currentSubscription)
                      .filter(e => e.beginner)
                      .map(offer =>
                      <div
                        className={ offerSelected === offer.index ? "offer-card-selected" : "offer-card" }
                        onClick={() => setOfferSelected(offer.index)}
                      >
                        <p className="offer-title">{offer.title}</p>
                        <ul className="offer-features">
                          {offer.featuresElements.map((feature, index) => (
                            <li key={index} dangerouslySetInnerHTML={{ __html: feature }} />
                          ))}
                        </ul>
                        <p className="offer-price">{offer.pricingElement}</p>
                      </div>
                    )
                  }
                </section>

                <section className="offer-row">
                  {
                    props.offers
                      .filter(e => !e.beginner)
                      .map(offer =>
                      <div
                        className={ offerSelected === offer.index ? "offer-card-selected" : "offer-card" }
                        onClick={() => setOfferSelected(offer.index)}
                      >
                        <p className="offer-title">{offer.title}</p>
                        <ul className="offer-features">
                          {offer.featuresElements.map((feature, index) => (
                            <li key={index} dangerouslySetInnerHTML={{ __html: feature }} />
                          ))}
                        </ul>
                        <p className="offer-price">{offer.pricingElement}</p>
                      </div>
                    )
                  }
                </section>

                {props.offers.some(offer => offer.commissionRate > 0) && (
                  <p className="commission-footnote">* Commission HT sur le prix des médias envoyés aux fans.</p>
                )}

                {
                  offerSelected > - 1 && (
                    <div ref={summupRef}>
                      <p className="new-bot-title">Récapitulatif</p>

                      <div className="summup-section">
                        <b></b>
                        <div className="summup-row">
                          <b>Compte Uncove</b>
                          <label>{uncoveName}</label>
                        </div>

                        <div className="summup-row">
                          <b>Futurs crédits messages</b>
                          <label>{formatMessagesRemaining(
                            Number(props.offers[offerSelected].features.messages || 0)
                            + Number(currentSubscription?.messagesRemaining || 0)
                          )}</label>
                        </div>

                        <br/>

                        <div className="summup-row">
                          <b>Montant à payer</b>
                          <label>{props.offers[offerSelected].pricing} €</label>
                        </div>
                      </div>
                    </div>
                  )
                }

                {
                  offerSelected > -1 &&
                  props.offers[offerSelected].keyname !== 'free' && (
                    <>
                      <p className="new-bot-title">Moyen de paiement</p>
                      <PaymentForm
                        onPaymentInitialized={() => setIsLoading(true)}
                        onPaymentError={() => setIsLoading(false)}
                        onPaymentSuccessfull={addBot}
                      />
                    </>
                  )
                }

                {
                  offerSelected > -1 && (
                    <div className="payment-buttons-container">
                      {props.offers[offerSelected].keyname !== 'free' ? (
                        <button type="submit" form="payment-form" className="new-bot-button" disabled={isLoading}>
                          {isLoading ? 'Chargement...' : 'Lancer votre agent'}
                        </button>
                      ) : (
                        <button type="submit" className="new-bot-button" onClick={addBot} disabled={isLoading}>
                          {isLoading ? 'Chargement...' : 'Lancer votre agent'}
                        </button>
                      )}
                      <button
                        className="button-outline"
                        onClick={() => {
                          setError('');
                          setNewBotMode(false);
                          setShowPaymentForm(false);
                          setCurrentSubscription(null);
                          setOfferSelected(-1);
                        }}
                      >
                        Retour
                      </button>
                    </div>
                  )
                }

                {error && <div className="error-message">{error}</div>}
              </div>
            }
          </>
        )
      }
    </div>
  );
};

export default Profile;
